import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABELS = {
  utilizzatore: "Utilizzatore",
  approvatore: "Approvatore",
  admin: "Amministratore",
};

export default function Profile() {
  const { user } = useAuth();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.next.length < 6) {
      toast.error("La nuova password deve avere almeno 6 caratteri");
      return;
    }
    if (form.next !== form.confirm) {
      toast.error("Le due password non coincidono");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current,
        new_password: form.next,
      });
      toast.success("Password aggiornata");
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-xl">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Il mio profilo</h1>
        <p className="mt-1 text-sm text-slate-500">Gestisci i tuoi dati di accesso.</p>
      </div>

      <Card className="mb-6 rounded-lg border-slate-200 p-6 shadow-sm">
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">Nome</dt>
            <dd className="mt-1 font-medium text-slate-900" data-testid="profile-name">{user?.name}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">Email</dt>
            <dd className="mt-1 font-medium text-slate-900" data-testid="profile-email">{user?.email}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">Ruolo</dt>
            <dd className="mt-1 font-medium text-slate-900" data-testid="profile-role">{ROLE_LABELS[user?.role] || user?.role}</dd>
          </div>
          {user?.fuel_card && (
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">Carta carburante</dt>
              <dd className="mt-1 font-medium text-slate-900" data-testid="profile-card">{user.fuel_card}</dd>
            </div>
          )}
        </dl>
      </Card>

      <Card className="rounded-lg border-slate-200 p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[#002FA7]/10 text-[#002FA7]">
            <KeyRound className="h-5 w-5" />
          </div>
          <div>
            <h2 className="font-heading text-lg font-bold text-slate-900">Cambia password</h2>
            <p className="text-sm text-slate-500">Aggiorna la password del tuo account.</p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="cur-pass">Password attuale</Label>
            <Input
              id="cur-pass"
              type="password"
              autoComplete="current-password"
              data-testid="current-password-input"
              value={form.current}
              onChange={(e) => setForm({ ...form, current: e.target.value })}
              required
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="new-pass">Nuova password</Label>
            <Input
              id="new-pass"
              type="password"
              autoComplete="new-password"
              data-testid="new-password-input"
              value={form.next}
              onChange={(e) => setForm({ ...form, next: e.target.value })}
              required
              className="mt-1.5"
              placeholder="min. 6 caratteri"
            />
          </div>
          <div>
            <Label htmlFor="confirm-pass">Conferma nuova password</Label>
            <Input
              id="confirm-pass"
              type="password"
              autoComplete="new-password"
              data-testid="confirm-password-input"
              value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              required
              className="mt-1.5"
            />
          </div>
          <Button type="submit" disabled={saving} data-testid="save-password-btn" className="rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Aggiorna password
          </Button>
        </form>
      </Card>
    </div>
  );
}
